import { Card, Stack, Title, Text, Group, Button } from '@repletoryreach/sdk-ui';
import { Tooltip } from '@repletoryreach/sdk-ui/overlays';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import type { admin } from '@repletoryreach/sdk-api';
import { useT } from '../../../../i18n/I18nProvider';

type AdminUser = NonNullable<Awaited<ReturnType<typeof admin.listUsers>>['data']>[number];

const MUTED = 'var(--rr-color-text-muted)';

/**
 * Transfer-organisation and hard-delete both land with W23.1.
 *
 * Until then the buttons are disabled. Opening a full destructive confirm that
 * resolves to "not implemented" is worse than no control at all: it is the
 * fastest way to teach an operator that confirm dialogs mean nothing.
 */
export function DangerTab({ user }: { user: AdminUser }) {
  const t = useT();

  return (
    <Stack gap="lg">
      {/* `red` is not a mapped Alert colour, so the strongest warning on the
          page used to render with no styling at all. */}
      <Alert color="error" variant="light">
        {t('users.view.danger.warning')}
      </Alert>

      <Card withBorder>
        <Stack gap="md">
          <Title order={4}>{t('users.view.danger.transfer.title')}</Title>
          <Text fz="sm" c={MUTED}>
            {t('users.view.danger.transfer.body')}
          </Text>
          <Group gap="sm">
            <PendingAction label={t('users.view.danger.transfer.action')} tooltip={t('users.view.danger.notAvailable.transfer')} />
          </Group>
        </Stack>
      </Card>

      <Card withBorder style={{ borderColor: 'var(--rr-color-error)' }}>
        <Stack gap="md">
          <Title order={4} c="var(--rr-color-error)">{t('users.view.danger.delete.title')}</Title>
          <Text fz="sm" c={MUTED}>
            {t('users.view.danger.delete.bodyLead')} <code>{user.email ?? user.display_name ?? t('users.unnamed')}</code> {t('users.view.danger.delete.bodyTrail')}
          </Text>
          <Group gap="sm">
            <PendingAction label={t('users.view.danger.delete.action')} tooltip={t('users.view.danger.notAvailable.delete')} />
          </Group>
          <Text fz="xs" c={MUTED}>{t('users.view.danger.notAvailable.explainer')}</Text>
        </Stack>
      </Card>
    </Stack>
  );
}

function PendingAction({ label, tooltip }: { label: string; tooltip: string }) {
  return (
    <Tooltip label={tooltip}>
      <span style={{ display: 'inline-flex' }}>
        <Button size="xs" variant="outline" disabled>{label}</Button>
      </span>
    </Tooltip>
  );
}
